import type { FileFilters } from '../types/file.types';
import { useFilesQuery } from './useFilesQuery';
import { useFilesTable } from './useFilesTable';
import { useFilesFilters } from './useFilesFilters';
import { useFilesDownload } from './useFilesDownload';
import { useFilesDragAndDrop } from './useFilesDragAndDrop';
import { useAppSelector } from '@/store/hooks';
import { useTour } from '@reactour/tour';
import { useMockFiles } from './useMockFiles';
import { useEffect } from 'react';

interface UseFilesReturn {
  table: ReturnType<typeof useFilesTable>['table'];
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
  downloadCsv: () => void;
  isDownloading: boolean;
  handleNextPage: () => void;
  handlePreviousPage: () => void;
  canPreviousPage: boolean;
  canNextPage: boolean;
  setSearchValue: (value: string) => void;
  searchValue: string;
  setOnlyMyDocs: (value: boolean) => void;
  onlyMyDocs: boolean;
  pageNb: number;
  pageSize: number;
  totalFiles: number;
  handleToggleFilter: (filter: FileFilters) => void;
  filters: FileFilters[];
  resetFilters: () => void;
  isReordering: boolean;
  handleDragEnd: (
    activeId: string,
    overId: string | null,
    items: string[]
  ) => Promise<void>;
  isDragEnabled: boolean;
}

export const useFiles = (): UseFilesReturn => {
  const { currentPage, pageSize, sorting } = useAppSelector(
    (state) => state.files
  );
  const { isOpen: isTourOpen, currentStep } = useTour();

  // Queries
  const {
    data: files,
    isLoading,
    isFetching,
    error,
    refetch,
  } = useFilesQuery();

  const {
    data: mockFiles,
    isLoading: mockIsLoading,
    isFetching: mockIsFetching,
    error: mockError,
    refetch: mockRefetch,
  } = useMockFiles();

  const documents = isTourOpen
    ? (mockFiles?.documents ?? [])
    : (files?.documents ?? []);

  const totalCount = isTourOpen
    ? (mockFiles?.total_count ?? 0)
    : (files?.total_count ?? 0);

  // Table
  const { table } = useFilesTable({
    data: documents,
    totalCount,
  });

  // Filters
  const {
    searchValue,
    onlyMyDocs,
    filters,
    setSearchValue,
    setOnlyMyDocs,
    toggleFilter: handleToggleFilter,
    resetFilters,
  } = useFilesFilters();

  // Download
  const { downloadCsv, isDownloading } = useFilesDownload();

  // Pagination handlers
  const handleNextPage = () => {
    table.nextPage();
  };

  const handlePreviousPage = () => {
    table.previousPage();
  };

  useEffect(() => {
    if (currentStep === 3) {
      resetFilters();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStep]);

  const isTableLoading = isTourOpen ? mockIsLoading : isLoading;
  const isTableFetching = isTourOpen ? mockIsFetching : isFetching;
  const tableError = isTourOpen ? mockError : error;
  const tableRefetch = isTourOpen ? mockRefetch : refetch;

  // Drag and Drop
  const { isReordering, handleDragEnd, isDragEnabled } = useFilesDragAndDrop({
    sorting,
    search: searchValue,
    onlyMe: onlyMyDocs,
    filters,
    onResetSelection: () => table.resetRowSelection(),
  });

  return {
    table,
    isLoading: isTableLoading,
    isFetching: isTableFetching,
    error: tableError,
    refetch: tableRefetch,
    downloadCsv,
    isDownloading,
    canPreviousPage: table.getCanPreviousPage(),
    canNextPage: table.getCanNextPage(),
    handleNextPage,
    handlePreviousPage,
    setSearchValue,
    searchValue,
    setOnlyMyDocs,
    onlyMyDocs,
    pageNb: currentPage + 1,
    pageSize,
    totalFiles: isTourOpen
      ? (mockFiles?.total_count ?? 0)
      : (files?.total_count ?? 0),
    handleToggleFilter,
    filters,
    resetFilters,
    isReordering,
    handleDragEnd,
    isDragEnabled,
  };
};
